import type {
  CmmdProviderAccountInventoryAccount,
  EnvironmentId,
  ModelSelection,
  ThreadId,
} from "@cmmd-forge/contracts";
import { Link } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import { AlertTriangleIcon } from "lucide-react";

import { runThreadExecutionIntentAction } from "../../lib/runThreadExecutionIntentAction";
import { newCommandId } from "../../lib/utils";
import type { RateLimitBannerWindow } from "./rateLimitBannerSelection";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Button } from "../ui/button";

const PROVIDER_LABEL: Record<"codex" | "claudeAgent", string> = {
  codex: "Codex",
  claudeAgent: "Claude",
};

function rateLimitBannerCopy(input: {
  readonly remainingPercent: number;
  readonly resetsLabel: string;
  readonly otherAccountEmail: string | null;
  readonly provider: "codex" | "claudeAgent";
  readonly inventoryResolved: boolean;
  readonly credits: number | null;
}): string {
  const base = `${input.remainingPercent}% remaining, resets ${input.resetsLabel}.`;
  // FOR-32770: "rate limit reached" is not the whole truth once the account
  // carries a pay-as-you-go balance - sends draw that down instead of
  // stopping, and the user should see the number before it does.
  const creditsSentence =
    input.provider === "codex" && input.credits !== null
      ? input.credits > 0
        ? ` Sending now draws down Codex credits: ${input.credits.toLocaleString("en-US")} left.`
        : " No Codex credits remain on this account."
      : "";
  if (input.otherAccountEmail !== null) {
    return `${base}${creditsSentence} Continue on ${input.otherAccountEmail} without losing this conversation.`;
  }
  // Until discovery resolves, "link another account" would be a claim about
  // accounts we have not managed to read yet.
  if (!input.inventoryResolved) return `${base}${creditsSentence}`;
  // Claude switching isn't wired yet (ClaudeAdapter.ts does not read
  // ModelSelection.providerAccountId) — "to keep going" would promise a
  // second account unblocks this thread right now, which linking one does
  // not do.
  return input.provider === "claudeAgent"
    ? `${base}${creditsSentence} Link another account for later, or wait for it to reset.`
    : `${base}${creditsSentence} Link another account to keep going, or wait for it to reset.`;
}

async function performAccountSwitch(input: {
  readonly environmentId: EnvironmentId;
  readonly projectId: number | null;
  readonly runtimeId?: string;
  readonly threadId: ThreadId;
  readonly modelSelection: ModelSelection;
  readonly otherAccount: CmmdProviderAccountInventoryAccount;
  readonly onSwitchingChange: (switching: boolean) => void;
  readonly onError: (message: string | null) => void;
}): Promise<void> {
  input.onSwitchingChange(true);
  input.onError(null);
  try {
    const command = {
      type: "thread.meta.update",
      commandId: newCommandId(),
      threadId: input.threadId,
      modelSelection: {
        ...input.modelSelection,
        providerAccountId: input.otherAccount.providerAccountId,
      } as ModelSelection,
    } as const;
    await runThreadExecutionIntentAction(
      input.environmentId,
      {
        ...(input.runtimeId ? { runtimeId: input.runtimeId } : {}),
        action: "wake",
        projectId: input.projectId,
        targetId: input.threadId,
        dispatchIdentity: command.commandId,
      },
      { dispatch: (api) => api.orchestration.dispatchCommand(command) },
    );
  } catch {
    input.onError("Couldn't switch accounts. Try again.");
  } finally {
    input.onSwitchingChange(false);
  }
}

/**
 * While the inventory is loading or has failed, `otherAccount` is undefined for
 * a reason that is NOT "you have no other account". Rendering the link action
 * then tells a user who does have a backup account to go create another one,
 * and hides the fact that discovery failed. Each outcome gets its own control.
 */
function RateLimitBannerAction(props: {
  readonly otherAccount: CmmdProviderAccountInventoryAccount | undefined;
  readonly inventoryState: "loading" | "error" | "ready";
  readonly isSwitching: boolean;
  readonly onSwitch: () => void;
  readonly onRetryInventory: () => void;
}) {
  if (!props.otherAccount && props.inventoryState === "loading") {
    return (
      <Button disabled size="sm" variant="outline">
        Checking your accounts…
      </Button>
    );
  }
  if (!props.otherAccount && props.inventoryState === "error") {
    return (
      <Button onClick={props.onRetryInventory} size="sm" variant="outline">
        Couldn't check your accounts — retry
      </Button>
    );
  }
  if (!props.otherAccount) {
    return (
      <Button
        render={<Link to="/settings/providers">Link another account</Link>}
        size="sm"
        variant="outline"
      />
    );
  }
  return (
    <Button disabled={props.isSwitching} onClick={props.onSwitch} size="sm">
      {props.isSwitching
        ? "Switching…"
        : `Switch to ${props.otherAccount.accountEmail ?? "other account"} and continue`}
    </Button>
  );
}

/**
 * A known-but-unnamed account still supports the switch, so it gets a generic
 * label rather than falling through to the "you have no other account" copy.
 */
function switchTargetLabel(
  account: CmmdProviderAccountInventoryAccount | undefined,
): string | null {
  if (!account) return null;
  return account.accountEmail ?? "your other account";
}

export function RateLimitWarningBanner(props: {
  readonly environmentId: EnvironmentId;
  readonly projectId: number | null;
  readonly runtimeId?: string;
  readonly threadId: ThreadId;
  readonly modelSelection: ModelSelection;
  readonly provider: "codex" | "claudeAgent";
  readonly status: RateLimitBannerWindow;
  readonly otherAccount: CmmdProviderAccountInventoryAccount | undefined;
  readonly credits: number | null;
  readonly inventoryState: "loading" | "error" | "ready";
  readonly onRetryInventory: () => void;
}) {
  const [isSwitching, setIsSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);

  const { status, otherAccount } = props;

  const switchAccount = async () => {
    if (!otherAccount) return;
    await performAccountSwitch({
      environmentId: props.environmentId,
      projectId: props.projectId,
      ...(props.runtimeId ? { runtimeId: props.runtimeId } : {}),
      threadId: props.threadId,
      modelSelection: props.modelSelection,
      otherAccount,
      onSwitchingChange: setIsSwitching,
      onError: setSwitchError,
    });
  };

  return (
    <div className="mx-auto w-full max-w-3xl px-4 pt-3">
      <RateLimitBannerAlert
        isRejected={status.status === "rejected"}
        label={PROVIDER_LABEL[props.provider]}
        copy={rateLimitBannerCopy({
          remainingPercent: Math.max(0, 100 - status.usedPercent),
          resetsLabel: formatRelativeTime(status.resetsAt),
          otherAccountEmail: switchTargetLabel(otherAccount),
          provider: props.provider,
          inventoryResolved: props.inventoryState === "ready",
          credits: props.credits,
        })}
        action={
          <RateLimitBannerAction
            otherAccount={otherAccount}
            inventoryState={props.inventoryState}
            isSwitching={isSwitching}
            onSwitch={() => void switchAccount()}
            onRetryInventory={props.onRetryInventory}
          />
        }
        switchError={switchError}
      />
    </div>
  );
}

function RateLimitBannerAlert(props: {
  readonly isRejected: boolean;
  readonly label: string;
  readonly copy: string;
  readonly action: ReactNode;
  readonly switchError: string | null;
}) {
  return (
    <Alert variant={props.isRejected ? "error" : "warning"}>
      <AlertTriangleIcon />
      <AlertTitle>
        {props.isRejected
          ? `${props.label} rate limit reached`
          : `${props.label} usage running high`}
      </AlertTitle>
      <AlertDescription>
        <p>{props.copy}</p>
        <div className="flex gap-2 pt-1">{props.action}</div>
        {props.switchError ? (
          <p className="pt-1 text-destructive-foreground text-xs">{props.switchError}</p>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}

function formatRelativeTime(iso: string): string {
  const targetMs = new Date(iso).getTime();
  if (Number.isNaN(targetMs)) return "soon";
  const diffMinutes = Math.round((targetMs - Date.now()) / 60_000);
  if (diffMinutes <= 1) return "in under a minute";
  if (diffMinutes < 60) return `in ${diffMinutes}m`;
  const hours = Math.round(diffMinutes / 60);
  if (hours < 48) return `in ${hours}h`;
  return `in ${Math.round(hours / 24)}d`;
}
